// src/components/practices/shared/PracticeStepRing.js
// Generalized version of Journal.js's august-only ".stops-ring-*" question-counter ring — same
// geometry, same getBBox() auto-fit/centering approach (font-dependent, not a dumb prop-driven
// SVG), same "-90deg progress SVG + separate unrotated numeral SVG" trick — but parameterized on
// `current`/`total` instead of Journal's hardcoded 10-question flow, and under its own class
// names (`practice-ring-*`) so this and Journal.js's copy can evolve independently. This is the
// third near-identical copy of this measurement logic in the codebase (after Journal.js's
// .stops-ring-* and AugustJourney.js's .aug-journey-ring-*) — consistent with existing
// precedent, not a new pattern. Factoring all three into one shared hook is a worthwhile future
// cleanup, not done here.
import React, { useLayoutEffect, useRef, useState } from 'react'
import './PracticeStepRing.css'

const RADIUS = 66.5
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
const STROKE = 7
const MAX_FONT = 2.5   // rem — ceiling
const MIN_FONT = 1.5   // rem — legibility floor
const FONT_STEP = 0.1  // rem
const INNER_DIAMETER = (RADIUS - STROKE / 2) * 2
const SAFE_WIDTH = INNER_DIAMETER * 0.68
const SAFE_HEIGHT = INNER_DIAMETER * 0.58

const pad = n => String(n).padStart(2, '0')

export default function PracticeStepRing({ current, total }) {
  const numRef = useRef(null)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [fontSize, setFontSize] = useState(MAX_FONT)

  useLayoutEffect(() => {
    const measure = () => {
      const el = numRef.current
      if (!el) return
      try {
        let size = MAX_FONT
        el.style.fontSize = `${size}rem`
        let bbox = el.getBBox()
        while ((bbox.width > SAFE_WIDTH || bbox.height > SAFE_HEIGHT) && size > MIN_FONT) {
          size = Math.max(MIN_FONT, +(size - FONT_STEP).toFixed(2))
          el.style.fontSize = `${size}rem`
          bbox = el.getBBox()
        }
        setFontSize(size)
        setOffset({ x: 70 - (bbox.x + bbox.width / 2), y: 70 - (bbox.y + bbox.height / 2) })
      } catch (e) { /* not laid out yet — next measure() call catches it */ }
    }
    measure()
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure)
    }
  }, [current, total])

  const ringOffset = CIRCUMFERENCE * (1 - current / total)

  return (
    <div className="practice-ring-wrap" data-city="august">
      <svg className="practice-ring-progress" viewBox="0 0 140 140" width="140" height="140">
        <circle className="practice-ring-track" cx="70" cy="70" r={RADIUS} fill="none" strokeWidth={STROKE} />
        <circle
          className="practice-ring-fill"
          cx="70" cy="70" r={RADIUS} fill="none" strokeWidth={STROKE}
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={ringOffset}
        />
      </svg>
      <svg className="practice-ring-num-svg" viewBox="0 0 140 140" width="140" height="140">
        <g transform={`translate(${offset.x}, ${offset.y})`}>
          <text
            ref={numRef}
            x="70" y="70"
            textAnchor="middle" dominantBaseline="middle"
            className="practice-ring-num"
            style={{ fontSize: `${fontSize}rem` }}
          >
            {pad(current)}
          </text>
        </g>
      </svg>
    </div>
  )
}
